// Attachment compression and validation for the family chat.
//
// Backend caps (per PRD + storage bucket limit):
//   • images:  2 MiB
//   • videos:  5 MiB
//   • GIFs:    5 MiB
//
// The bucket's file_size_limit is set to 5 MiB so the database will reject
// anything larger. We still compress images client-side to honour the
// tighter 2 MiB cap, and to make uploads fast over slow connections.
// The `captureStream()` method on HTMLVideoElement is widely supported in
// browsers (Chrome/Edge/Firefox/Safari 16.4+) but not in the current Bun
// TS lib. Augment the interface so callers stay typed without `any`.
declare global {
  interface HTMLVideoElement {
    captureStream(frameRate?: number): MediaStream;
  }
}
export const LIMITS = {
  imageMaxBytes: 2 * 1024 * 1024,
  videoMaxBytes: 5 * 1024 * 1024,
  gifMaxBytes:   5 * 1024 * 1024,
} as const;

export type AttachmentKind = "image" | "video" | "gif";

export type CompressionResult = {
  file:  Blob;
  mime:  string;
  /** Approximate bytes after compression. */
  size:  number;
};

/** Heuristic classify of a file. Falls back to extension on missing type. */
export function classifyFile(file: File): AttachmentKind {
  const t = file.type.toLowerCase();
  if (t === "image/gif") return "gif";
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  const name = file.name.toLowerCase();
  if (name.endsWith(".gif")) return "gif";
  if (/\.(png|jpe?g|webp|heic|heif|bmp)$/.test(name)) return "image";
  if (/\.(mp4|mov|webm|m4v|3gp)$/.test(name)) return "video";
  // Default: treat unknown as image — backend will reject if it's not.
  return "image";
}

export function maxBytesFor(kind: AttachmentKind): number {
  switch (kind) {
    case "image": return LIMITS.imageMaxBytes;
    case "video": return LIMITS.videoMaxBytes;
    case "gif":   return LIMITS.gifMaxBytes;
  }
}

/**
 * Downscale an image to fit within `maxEdge` and re-encode as JPEG. The
 * browser's canvas takes care of the actual pixel work. We ramp the quality
 * down if the result is still over the per-kind cap.
 */
export async function compressImage(
  file: File,
  opts: { maxEdge?: number; capBytes: number } = { capBytes: LIMITS.imageMaxBytes },
): Promise<CompressionResult> {
  const maxEdge = opts.maxEdge ?? 1600;
  const url = URL.createObjectURL(file);

  try {
    const img = await loadImage(url);
    const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, maxEdge);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("瀏覽器不支援 canvas 2D，無法壓縮圖片。");
    ctx.drawImage(img, 0, 0, width, height);

    // Try a quality ramp so the result fits the per-kind cap.
    const qualities = [0.82, 0.7, 0.55, 0.4] as const;
    for (const q of qualities) {
      const blob = await canvasToBlob(canvas, "image/jpeg", q);
      if (blob.size <= opts.capBytes) {
        return { file: blob, mime: "image/jpeg", size: blob.size };
      }
      // Keep the smallest we've produced so far; if none fit, fall through.
    }
    const final = await canvasToBlob(canvas, "image/jpeg", 0.4);
    if (final.size > opts.capBytes) {
      throw new Error(
        `圖片壓縮後仍超過 ${(opts.capBytes / 1024 / 1024).toFixed(1)} MiB 上限，請改用較小的圖片。`,
      );
    }
    return { file: final, mime: "image/jpeg", size: final.size };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Transcode a video to webm via MediaRecorder. This is a best-effort path:
 * Safari/iOS often lack `video/webm;codecs=vp9` support, in which case we
 * fall back to the original file and let the backend's 5 MiB cap decide.
 */
export async function compressVideo(
  file: File,
  opts: { capBytes: number } = { capBytes: LIMITS.videoMaxBytes },
): Promise<CompressionResult> {
  if (file.size <= opts.capBytes) {
    return { file, mime: file.type || "video/mp4", size: file.size };
  }
  if (typeof MediaRecorder === "undefined") {
    return { file, mime: file.type || "video/mp4", size: file.size };
  }

  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  const mimeType = candidates.find(t =>
    typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(t),
  );
  if (!mimeType) {
    // Browser can't record. Hand off the original; backend will reject if too big.
    return { file, mime: file.type || "video/mp4", size: file.size };
  }

  const url = URL.createObjectURL(file);
  try {
    const video = await loadVideo(url);
    const stream = video.captureStream();
    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 800_000 });
    const chunks: Blob[] = [];
    const { promise, resolve, reject } = Promise.withResolvers<CompressionResult>();
    recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      // If our transcode ended up larger than the cap, fall back to original.
      const out: CompressionResult = blob.size <= file.size
        ? { file: blob, mime: mimeType, size: blob.size }
        : { file, mime: file.type || "video/mp4", size: file.size };
      resolve(out);
    };
    recorder.onerror = e => reject(new Error(`影片轉檔失敗：${(e as ErrorEvent).message ?? "unknown"}`));
    recorder.start(250);
    await video.play();
    await waitFor(video, "ended");
    recorder.stop();
    return await promise;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ---------- low-level helpers ----------

function fitWithin(w: number, h: number, maxEdge: number) {
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
  const scale = Math.min(maxEdge / w, maxEdge / h);
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

function loadImage(url: string): Promise<HTMLImageElement> {
  const { promise, resolve, reject } = Promise.withResolvers<HTMLImageElement>();
  const img = new Image();
  img.onload  = () => resolve(img);
  img.onerror = () => reject(new Error("圖片讀取失敗，檔案可能已損壞。"));
  img.src = url;
  return promise;
}

function loadVideo(url: string): Promise<HTMLVideoElement> {
  const { promise, resolve, reject } = Promise.withResolvers<HTMLVideoElement>();
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.onloadedmetadata = () => resolve(video);
  video.onerror = () => reject(new Error("影片讀取失敗，檔案可能已損壞或不支援。"));
  video.src = url;
  return promise;
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  const { promise, resolve, reject } = Promise.withResolvers<Blob>();
  try {
    // Bug #14: the previous literal "canvas.toBlob 失敗" was unhelpful
    // when the canvas was tainted by a cross-origin image (browsers
    // either return null silently or throw SecurityError). The
    // synchronous `try/catch` covers Chromium/Firefox which throw on
    // tainted canvases; the null-result branch catches Safari which
    // returns null instead. We pick the same friendly message in both
    // cases — it's the overwhelmingly common reason for failure here,
    // and the user action ("改用一般相片") is correct regardless of
    // the underlying cause.
    canvas.toBlob(
      blob => blob
        ? resolve(blob)
        : reject(new Error("圖片來源不被允許（可能被瀏覽器標記為跨來源），請改用一般相片。")),
      type,
      quality,
    );
  } catch (err) {
    if (err instanceof DOMException && err.name === "SecurityError") {
      reject(new Error("圖片來源不被允許（可能被瀏覽器標記為跨來源），請改用一般相片。"));
    } else {
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  }
  return promise;
}

function waitFor(el: HTMLMediaElement, event: string): Promise<void> {
  const { promise, resolve, reject } = Promise.withResolvers<void>();
  el.addEventListener(event, () => resolve(), { once: true });
  el.addEventListener("error", () => reject(new Error("播放時發生錯誤")), { once: true });
  return promise;
}
